import { apiFetch, downloadFile, openFileInNewTab } from './client';
import type {
  Paginated,
  Quotation,
  QuotationDetail,
  QuotationSection,
  LineItem,
  ClientComment,
  ShareInfo,
  QuotationVersion,
  QuotationTerm
} from '$lib/types';

function toQuery(params: Record<string, string | number | undefined> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export const quotationsApi = {
  list: (params?: {
    status?: string;
    vertical?: string;
    q?: string;
    page?: number;
    per_page?: number;
    archived?: '1';
    project_id?: number | 'none';
  }) => apiFetch<Paginated<Quotation>>(`/quotations${toQuery(params)}`),
  get: (id: number) => apiFetch<QuotationDetail>(`/quotations/${id}`),
  assignProject: (id: number, project_id: number | null) =>
    apiFetch<Quotation>(`/quotations/${id}/project`, { method: 'PUT', body: JSON.stringify({ project_id }) }),
  create: (body: Record<string, unknown>) =>
    apiFetch<QuotationDetail>('/quotations', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Record<string, unknown>) =>
    apiFetch<QuotationDetail>(`/quotations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<{ detail: string }>(`/quotations/${id}`, { method: 'DELETE' }),
  archive: (id: number) => apiFetch<{ detail: string }>(`/quotations/${id}/archive`, { method: 'POST' }),
  unarchive: (id: number) => apiFetch<{ detail: string }>(`/quotations/${id}/unarchive`, { method: 'POST' }),

  submit: (id: number) => apiFetch<Quotation>(`/quotations/${id}/submit`, { method: 'POST' }),
  price: (id: number) => apiFetch<QuotationDetail>(`/quotations/${id}/price`, { method: 'POST' }),
  approve: (id: number) => apiFetch<Quotation>(`/quotations/${id}/approve`, { method: 'POST' }),
  returnForPricing: (id: number) =>
    apiFetch<Quotation>(`/quotations/${id}/return-for-pricing`, { method: 'POST' }),
  revise: (id: number) => apiFetch<Quotation>(`/quotations/${id}/revise`, { method: 'POST' }),

  createSection: (quotationId: number, body: Record<string, unknown>) =>
    apiFetch<QuotationSection>(`/quotations/${quotationId}/sections`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateSection: (quotationId: number, sectionId: number, body: Record<string, unknown>) =>
    apiFetch<QuotationSection>(`/quotations/${quotationId}/sections/${sectionId}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteSection: (quotationId: number, sectionId: number) =>
    apiFetch<{ detail: string }>(`/quotations/${quotationId}/sections/${sectionId}`, { method: 'DELETE' }),
  duplicateSection: (quotationId: number, sectionId: number) =>
    apiFetch<QuotationSection>(`/quotations/${quotationId}/sections/${sectionId}/duplicate`, { method: 'POST' }),

  createLineItem: (quotationId: number, sectionId: number, body: Record<string, unknown>) =>
    apiFetch<LineItem>(`/quotations/${quotationId}/sections/${sectionId}/line-items`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
  updateLineItem: (quotationId: number, sectionId: number, lineItemId: number, body: Record<string, unknown>) =>
    apiFetch<LineItem>(`/quotations/${quotationId}/sections/${sectionId}/line-items/${lineItemId}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteLineItem: (quotationId: number, sectionId: number, lineItemId: number) =>
    apiFetch<{ detail: string }>(`/quotations/${quotationId}/sections/${sectionId}/line-items/${lineItemId}`, {
      method: 'DELETE'
    }),

  downloadPdf: (id: number, formattedNumber: string, showDiscount = false) =>
    downloadFile(`/quotations/${id}/pdf${showDiscount ? '?show_discount=1' : ''}`, {}, `${formattedNumber}.pdf`),
  previewPdf: (id: number, showDiscount = false) =>
    openFileInNewTab(`/quotations/${id}/pdf?inline=1${showDiscount ? '&show_discount=1' : ''}`),

  downloadXlsx: (id: number, formattedNumber: string) =>
    downloadFile(`/quotations/${id}/xlsx`, {}, `${formattedNumber}.xlsx`),
  downloadBOQXlsx: (id: number, formattedNumber: string) =>
    downloadFile(`/quotations/${id}/boq.xlsx`, {}, `${formattedNumber}-BOQ.xlsx`),

  duplicate: (id: number) => apiFetch<QuotationDetail>(`/quotations/${id}/duplicate`, { method: 'POST' }),
  newLot: (id: number) => apiFetch<QuotationDetail>(`/quotations/${id}/new-lot`, { method: 'POST' }),

  listComments: (quotationId: number) =>
    apiFetch<{ results: ClientComment[]; count: number }>(`/quotations/${quotationId}/comments`),
  createComment: (quotationId: number, body: string) =>
    apiFetch<ClientComment>(`/quotations/${quotationId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body })
    }),

  createShareLink: (id: number, password?: string) =>
    apiFetch<ShareInfo>(`/quotations/${id}/share`, {
      method: 'POST',
      body: JSON.stringify(password ? { password } : {})
    }),
  revokeShareLink: (id: number) => apiFetch<{ detail: string }>(`/quotations/${id}/share`, { method: 'DELETE' }),
  updateSharePassword: (id: number, password: string) =>
    apiFetch<ShareInfo>(`/quotations/${id}/share/password`, {
      method: 'PUT',
      body: JSON.stringify({ password })
    }),

  applyTermsTemplate: (quotationId: number, templateId: number) =>
    apiFetch<QuotationTerm>(`/quotations/${quotationId}/terms/apply`, {
      method: 'POST',
      body: JSON.stringify({ template_id: templateId })
    }),
  createTerm: (quotationId: number, body: { category: string; title: string; body: string }) =>
    apiFetch<QuotationTerm>(`/quotations/${quotationId}/terms`, { method: 'POST', body: JSON.stringify(body) }),
  updateTerm: (quotationId: number, termId: number, body: Record<string, unknown>) =>
    apiFetch<QuotationTerm>(`/quotations/${quotationId}/terms/${termId}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  deleteTerm: (quotationId: number, termId: number) =>
    apiFetch<{ detail: string }>(`/quotations/${quotationId}/terms/${termId}`, { method: 'DELETE' }),
  reorderTerms: (quotationId: number, termIds: number[]) =>
    apiFetch<{ detail: string }>(`/quotations/${quotationId}/terms/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ term_ids: termIds })
    }),

  // Admin-only version history (see internal/api/handlers/quotation_versions.go).
  listVersions: (id: number) => apiFetch<{ results: QuotationVersion[] }>(`/quotations/${id}/versions`),
  downloadVersionPdf: (id: number, versionNumber: number, formattedVersion: string) =>
    downloadFile(`/quotations/${id}/versions/${versionNumber}/pdf`, {}, `${formattedVersion}.pdf`)
};
